<template>
  <div class="tiers-settings settings">
    <header>
      <h1 class="page-header">Tiers</h1>
    </header>
    <div v-for="(tier, index) in tiers">
      <label>
        Tier {{ index + 1 }}:
      </label>
      <grid-selector
        v-if="!shortDeck"
        v-bind:generalHands="tier.generalHands"
        v-bind:specificHandsNotAllowed="true"
        v-on:selectGeneralHand="selectGeneralHand($event.generalHandString, tier.generalHands)"
        v-on:deselectGeneralHand="deselectGeneralHand($event, tier.generalHands)"
        v-on:selectGeneralHands="selectGeneralHands($event, tier.generalHands)"
        v-on:deselectGeneralHands="deselectGeneralHands($event, tier.generalHands)"
      />
      <short-deck-grid-selector
        v-if="shortDeck"
        v-bind:generalHands="tier.generalHands"
        v-bind:specificHandsNotAllowed="true"
        v-on:selectGeneralHand="selectGeneralHand($event.generalHandString, tier.generalHands)"
        v-on:deselectGeneralHand="deselectGeneralHand($event, tier.generalHands)"
        v-on:selectGeneralHands="selectGeneralHands($event, tier.generalHands)"
        v-on:deselectGeneralHands="deselectGeneralHands($event, tier.generalHands)"
      />
      <hr>
    </div>
    <section class="buttons">
      <button class="btn btn-default btn-lg" v-on:click="isHelpVisible = !isHelpVisible">
        <span v-if="!isHelpVisible">Show help <span class="caret-down"></span></span>
        <span v-if="isHelpVisible">Hide help <span class="caret-up"></span></span>
      </button>
      <button type="button" class="btn btn-default btn-lg" v-on:click="reset()">Set to defaults</button>
      <button type="button" class="btn btn-primary btn-lg" v-on:click="save()">Save</button>
    </section>
    <section v-if="isHelpVisible" class="well">
      <p>When you click one of the tiers highlighted in the picture below, it will add/remove hands from the range based on how the tiers are set above.</p>
      <p>The default tiers come from the <a href="https://en.wikipedia.org/wiki/Texas_hold_%27em_starting_hands#Sklansky_hand_groups">Sklansky hand groups</a>.</p>
      <img src="/static/settings/tiers/tiers.png">
    </section>
  </div>
</template>

<script>
import GridSelector from '@/components/hand-range/grid-selector/grid-selector';
import ShortDeckGridSelector from '@/components/short-deck/short-deck-grid-selector';
import GeneralHands from '@/services/hands/GeneralHands';
import settingsValidators from '@/services/settings-validators';
import _ from 'lodash';

export default {
  name: 'tiers-settings',
  components: {
    'grid-selector': GridSelector,
    'short-deck-grid-selector': ShortDeckGridSelector,
  },
  data: function () {
    return {
      tiers: null,
      isHelpVisible: false,
    };
  },
  computed: {
    shortDeck: function () {
      return this.$store.getters['account/purchasedShortDeck'];
    },
  },
  mounted: function () {
    this.initialize();
  },
  methods: {
    // initialize
    initialize: function () {
      let tiers = this._constructTiers();

      this._setHandsInRangeForTiers(tiers);
      this.tiers = tiers;
    },
    _constructTiers: function () {
      let tiers = [];

      _.times(8, function () {
        tiers.push({
          generalHands: new GeneralHands(),
        });
      });

      return tiers;
    },
    _setHandsInRangeForTiers: function (tiers) {
      tiers.forEach(function (tier, i) {
        this.__setHandsInRangeForTier(tier, i);
      }, this);
    },
    __setHandsInRangeForTier: function (tier, i) {
      tier.generalHands.forEach(function (generalHand) {
        generalHand.forEachSpecificHandInRange(function (specificHand) {
          specificHand.weight = 0;
        });
      });
      this.$store.state.account.user.settings.general.tiers[i].forEach(function (generalHandString) {
        this.selectGeneralHand(generalHandString, tier.generalHands);
      }, this);
    },

    // range
    selectGeneralHand: function (generalHandString, generalHands) {
      let generalHand = generalHands.find(generalHandString);

      generalHand.forEachPossibleSpecificHand(function (specificHand) {
        if (!specificHand.inRange) {
          specificHand.weight = 100;
        }
      });
    },
    deselectGeneralHand: function (generalHandString, generalHands) {
      let generalHand = generalHands.find(generalHandString);

      generalHand.forEachSpecificHandInRange(function (specificHand) {
        specificHand.weight = 0;
      });
    },
    selectGeneralHands: function (generalHandStrings, generalHands) {
      generalHandStrings.forEach(function (generalHandString) {
        this.selectGeneralHand(generalHandString, generalHands);
      }, this);
    },
    deselectGeneralHands: function (generalHandStrings, generalHands) {
      generalHandStrings.forEach(function (generalHandString) {
        this.deselectGeneralHand(generalHandString, generalHands);
      }, this);
    },

    // buttons
    save: function () {
      let self = this;
      let user = _.cloneDeep(this.$store.state.account.user);
      let tiersGeneralHandsInRangeArray = this._getTiersGeneralHandsInRangeArray();

      if (!settingsValidators.areTiersValid(tiersGeneralHandsInRangeArray)) {
        alert('Make sure that you didn\'t leave any of the ranges empty.');
        return;
      }

      user.settings.general.tiers = tiersGeneralHandsInRangeArray;
      self.$http.put(`${process.env.BASE_API_URL}/user`, { user: user }).then(function (res) {
        self.$store.commit('account/setUser', res.data.user);
        alert('Saved!');
      }).catch(function () {
        alert('Unable to save settings.');
      });
    },
    _getTiersGeneralHandsInRangeArray: function () {
      let tiersGeneralHandsInRangeArray = [];

      this.tiers.forEach(function (tier, i) {
        tiersGeneralHandsInRangeArray[i] = [];
        tier.generalHands.forEach(function (generalHand) {
          if (generalHand.inRange) {
            tiersGeneralHandsInRangeArray[i].push(generalHand.string);
          }
        });
      });

      return tiersGeneralHandsInRangeArray;
    },
    reset: function () {
      let self = this;
      let user = _.cloneDeep(this.$store.state.account.user);
      let defaultTiersGeneralHandsInRangeArray = [
        ['aa','kk','qq','jj','aks'],
        ['tt','aqs','ajs','kqs','ako'],
        ['99','ats','kjs','qjs','jts','aqo'],
        ['88','kts','qts','j9s','t9s','98s','ajo','kqo'],
        ['77','a9s','a8s','a7s','a6s','a5s','a4s','a3s','a2s','q9s','t8s','97s','87s','76s','kjo','qjo','jto'],
        ['66','55','k9s','j8s','86s','75s','54s','ato','kto','qto'],
        ['44','33','22','k8s','k7s','k6s','k5s','k4s','k3s','k2s','q8s','t7s','65s','64s','53s','43s','j9o','t9o','98o'],
        ['j7s','96s','85s','74s','42s','32s','a9o','k9o','q9o','j8o','t8o','87o','76o','65o','54o']
      ];

      user.settings.general.tiers = defaultTiersGeneralHandsInRangeArray;
      self.$http.put(`${process.env.BASE_API_URL}/user`, { user: user }).then(function (res) {
        self.$store.commit('account/setUser', res.data.user);
        self._setHandsInRangeForTiers(self.tiers);
        alert('Saved!');
      }).catch(function () {
        alert('Unable to save settings.');
      });
    },
  },
}
</script>

<style lang="scss" scoped>
  @import 'src/assets/settings';

  img {
    margin-bottom: 10px;
    width: 400px;
  }
</style>



// WEBPACK FOOTER //
// src/components/settings/general/tiers.vue